<script setup lang="ts">
import { ref, watch } from 'vue';
import { SpButton, notify } from '@servepod/ui';
import { ApiError, admin } from '../lib/api';

/**
 * The step-up dialog. Opened by the api layer whenever any request is refused
 * with MFA_REQUIRED; a passed challenge stamps the CURRENT session, after which
 * the operator retries the action that summoned it — retrying arbitrary
 * mutations automatically would re-fire side effects nobody re-reviewed.
 */

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const code = ref('');
const busy = ref(false);
const failed = ref(false);
/**
 * Why it failed, in the operator's terms. A bare catch showed "The code was not
 * accepted." for three genuinely different situations — a typo, a lockout, and a
 * server that cannot decrypt its own TOTP secrets — so the two that have specific
 * remedies (wait; use a recovery code) read as the one that does not.
 */
const failure = ref('');

watch(
  () => props.open,
  (open) => {
    if (open) {
      code.value = '';
      failed.value = false;
      failure.value = '';
    }
  },
);

async function submit() {
  if (code.value.trim().length < 6 || busy.value) return;
  busy.value = true;
  failed.value = false;
  try {
    await admin.mfaChallenge(code.value.trim());
    notify.success('Verified', 'This session is elevated — retry the action.');
    emit('close');
  } catch (error) {
    failed.value = true;
    const code = error instanceof ApiError ? error.code : '';
    failure.value =
      code === 'RATE_LIMITED'
        ? 'Too many failed codes. Wait a minute, then try again.'
        : code === 'DEPENDENCY_UNAVAILABLE'
          ? 'One-time codes are unavailable right now — use a recovery code.'
          : 'The code was not accepted.';
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Two-factor verification"
    >
      <div class="w-full max-w-sm rounded-lg border border-line bg-surface p-5 shadow-lg">
        <h2 class="text-sm font-semibold tracking-tight">Verification required</h2>
        <p class="mt-1 text-xs text-ink-secondary">
          That action is protected. Enter the six-digit code from your authenticator app (or a
          recovery code), then retry the action.
        </p>
        <input
          v-model="code"
          inputmode="numeric"
          autofocus
          placeholder="123456"
          class="mt-3 w-full rounded border border-line bg-surface-raised px-3 py-2 font-mono text-sm"
          @keyup.enter="submit"
        />
        <p v-if="failed" class="mt-1 text-xs text-danger">{{ failure }}</p>
        <div class="mt-4 flex justify-end gap-2">
          <SpButton variant="ghost" size="sm" @click="emit('close')">Cancel</SpButton>
          <SpButton size="sm" :disabled="busy || code.trim().length < 6" @click="submit">
            Verify
          </SpButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>
